import { ReactElement } from 'react';

import { appColors, Tier, CareerCategory } from '@backrs_ui/common-ui';

import {
  CareerUserResponseSet,
  CareerQuestion,
  CareerPathWithScore,
} from '@backrs_ui/grow-ui/src/models/models';

import { Text } from '@react-pdf/renderer';

/** makes a map of question title and responses joined with a comma, like Building Wealth: Very Important */
export const makeAspirationMap = (
  userResponseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
  returnType: 'PDF' | 'WEB',
): Record<number, ReactElement> => {
  return questions.reduce(
    (acc, question) => {
      const userResponse = userResponseSet.user_responses.find(
        (response) => response.career_question_id === question.id,
      );

      if (!userResponse) {
        return acc;
      }

      const selectedOptions = question.options.filter((option) =>
        userResponse.career_question_option_ids.includes(option.id),
      );

      if (returnType === 'WEB') {
        acc[question.id] = (
          <>
            <strong style={{ color: appColors.color.blue1 }}>
              {question.aspirational_title || question.title}:
            </strong>{' '}
            {question.is_custom_response
              ? userResponse.career_question_option_custom_text
              : selectedOptions
                  .map((option) => option.aspirational_title || option.answer)
                  .join(', ')}
          </>
        );
      }

      if (returnType === 'PDF') {
        acc[question.id] = (
          <Text>
            <Text style={{ color: '#367FFF', fontWeight: 'bold' }}>
              {question.aspirational_title || question.title}:
            </Text>{' '}
            {question.is_custom_response
              ? userResponse.career_question_option_custom_text
              : selectedOptions
                  .map((option) => option.aspirational_title || option.answer)
                  .join(', ')}
          </Text>
        );
      }

      return acc;
    },
    {} as Record<number, ReactElement>,
  );
};

/** First we calculate group size, so we can determine the top third, middle third, and bottom third of sums in the list. Then we get the blue1 color, but a different opacity depending on where it lands */
export const getCareerPathFitColor = (
  scoreSection: CareerPathWithScore['scoreSection'],
): string => {
  if (scoreSection === 'TOP') {
    return appColors.color.navy2;
  }

  if (scoreSection === 'MIDDLE') {
    return appColors.color.blue1;
  }

  return `${appColors.color.blue1}36`; // 21% in Figma, converted to hex is 36
};

/** Determine which tiers should be selected, i.e., if we are middle tier we should select all tiers above */
export const isTierSelected = (boxTier: Tier, minimumTrack: Tier): boolean => {
  const tierRankMap: Record<Tier, number> = {
    'Semi-skilled': 1,
    Trades: 2,
    'Middle-Tier Professional': 3,
    'Upper-Tier Professional': 4,
    'Top-Tier Professional': 5,
    Entrepreneurship: 6,
  };

  return tierRankMap[boxTier] >= tierRankMap[minimumTrack];
};

export const getEntrepreneurshipGradientPercentage = (
  minimumTrack: Tier,
  entrepreneurshipRecommended: boolean,
  returnType: 'PDF' | 'WEB',
): number => {
  if (!entrepreneurshipRecommended) {
    return 0;
  }

  const entrepreneurshipGradientMapWeb: Record<Tier, number> = {
    'Upper-Tier Professional': 90,
    'Top-Tier Professional': 45,
    // for the others, we just want to fill the entire thing so the gradient is not visible. We do have to include them in the map though for TypeScript
    Trades: 100,
    'Middle-Tier Professional': 100,
    'Semi-skilled': 100,
    Entrepreneurship: 0,
  };

  const entrepreneurshipGradientMapPDF: Record<Tier, number> = {
    'Upper-Tier Professional': 0.8,
    'Top-Tier Professional': 0.35,
    Trades: 1,
    'Middle-Tier Professional': 1,
    'Semi-skilled': 1,
    Entrepreneurship: 1,
  };

  return returnType === 'WEB'
    ? entrepreneurshipGradientMapWeb[minimumTrack]
    : entrepreneurshipGradientMapPDF[minimumTrack];
};

/** Sorts the career path suggestions by the passed in category, but keeps the original array index so we can correctly color the circles */
export const sortCareerSuggestions = (
  careerPathSuggestions: CareerPathWithScore[],
  category: CareerCategory,
) => {
  const filteredCareerPathWithOriginalIndex = careerPathSuggestions
    // first we filter out the paths that don't belong to the category
    .reduce<{ path: CareerPathWithScore; originalIndex: number }[]>((acc, path, originalIndex) => {
      if (path.careerPath.career_category_id === category.id) {
        return [...acc, { path, originalIndex }];
      }

      return acc;
    }, [])
    // then we sort the remaining alphabetically by name
    .sort((a, b) => a.path.careerPath.name.localeCompare(b.path.careerPath.name));

  return filteredCareerPathWithOriginalIndex;
};
